import { shouldClearSession } from "./requestErrors";
import axios from "axios";
import { APP_VERSION } from "../../config/version";
import { companyInfo } from "../config/companyInfo";

const baseURL = process.env.REACT_APP_BASE_URL;

// Active platform issuing company (S22) — fetched from the backend at app
// start (GET /admin/sii/platform_company) so the admin can switch the issuer
// without a redeploy. Falls back to REACT_APP_INVOICE_EMPRESA_ID / 1.
let activeEmpresaId = null;
export const setActiveEmpresaId = (id) => {
  const parsed = Number(id);
  activeEmpresaId = Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};
export const getActiveEmpresaId = () => activeEmpresaId ?? companyInfo.empresaId;

// Store the last received header version for debugging
let lastHeaderVersion = null;
export const getLastHeaderVersion = () => lastHeaderVersion;

// Function to check app version from response headers (background process)
const checkAppVersion = (response) => {
  const serverVersion = response?.headers?.['x-admin-app-version'];
  
  // Only process if we have a server version
  if (!serverVersion) return;
  
  // Store for debugging
  lastHeaderVersion = serverVersion;
  
  // Log for debugging (can be removed in production)
  console.log(`[Version Check] Header: ${serverVersion}, Local: ${APP_VERSION}, Match: ${serverVersion === APP_VERSION}`);
  
  if (serverVersion !== APP_VERSION) {
    // Version mismatch - dispatch event to show update banner
    window.dispatchEvent(new CustomEvent('app-version-mismatch', {
      detail: { serverVersion, currentVersion: APP_VERSION }
    }));
  } else {
    // Version matches - dispatch event to hide update banner (if shown)
    window.dispatchEvent(new CustomEvent('app-version-match', {
      detail: { serverVersion, currentVersion: APP_VERSION }
    }));
  }
};
// const baseURL = process.env.REACT_APP_DEV_BASE_URL;
// const baseURL = process.env.REACT_APP_LOCAL_URL;

const serialize = function (obj) {
  var str = [];
  for (var p in obj)
    if (obj.hasOwnProperty(p)) {
      str.push(encodeURIComponent(p) + "=" + encodeURIComponent(obj[p]));
    }
  return str.join("&");
};

const toObject = (str) => {
  let foo = str.split("&").reduce(function (obj, str, index) {
    let strParts = str.split("=");
    if (strParts[0] && strParts[1]) {
      obj[strParts[0].replace(/\s+/g, "")] = strParts[1].trim(); // Get rid of extra spaces at beginning of value strings
    }
    return obj;
  }, {});
  return foo;
};

const updateURL = (query) => {
  let obj = toObject(query);
  for (let param in obj) { // eslint-disable-line no-unused-vars
    if (
      obj[param] === undefined || // In case of undefined assignment
      obj[param] === null ||
      obj[param] === ""
    ) {
      delete obj[param];
    }
  }
  return serialize(obj).toString();
};

// Helper to check if URL is valid for redirect
const isValidRedirectUrl = (url) => {
  if (!url) return false;
  // Don't redirect to login page or empty paths
  const invalidPaths = ['/user-pages/login-1', '/user-pages/login', '/login', '/', ''];
  return !invalidPaths.includes(url);
};

// Function to save intended URL before logout
const saveIntendedUrl = () => {
  const currentPath = window.location.pathname + window.location.search;
  
  // Check if this is the URL we just tried to redirect to after login
  const lastAttemptedRedirect = sessionStorage.getItem('lastAttemptedRedirect');
  if (lastAttemptedRedirect === currentPath) {
    // The redirect we just tried failed - user doesn't have access
    // Mark it as failed so we don't try again
    console.log(`[Redirect] Access denied to ${currentPath} - marking as failed`);
    sessionStorage.setItem('redirectAfterLogin', currentPath);
    sessionStorage.setItem('redirectAttempts', '1'); // Mark as failed once
    sessionStorage.removeItem('lastAttemptedRedirect');
    return;
  }
  
  if (!isValidRedirectUrl(currentPath)) {
    return;
  }
  
  // Check if we already have a saved redirect
  const existingUrl = sessionStorage.getItem('redirectAfterLogin');
  const redirectAttempts = parseInt(sessionStorage.getItem('redirectAttempts') || '0', 10);
  
  if (existingUrl === currentPath) {
    // Same URL is failing again - likely a permissions issue
    if (redirectAttempts >= 1) {
      // Already failed once, clear everything to prevent infinite loop
      console.log(`[Redirect] ${currentPath} failed multiple times - clearing redirect`);
      sessionStorage.removeItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAttempts');
      sessionStorage.removeItem('lastAttemptedRedirect');
      return;
    }
    // Increment attempt counter
    sessionStorage.setItem('redirectAttempts', (redirectAttempts + 1).toString());
  } else if (!existingUrl) {
    // First 401 - save the URL
    console.log(`[Redirect] Saving redirect URL: ${currentPath}`);
    sessionStorage.setItem('redirectAfterLogin', currentPath);
    sessionStorage.setItem('redirectAttempts', '0');
  }
};

// Guards against re-entrant teardown when several requests 401 at once.
let isHandlingUnauthorized = false;

// Function to clear local storage and cookies, then reload the page
const handleUnauthorized = (error) => {
  // No active session → nothing to tear down. A 401 with no token is just an
  // unauthenticated request (a failed login, or a stray bootstrap call sitting
  // on the login page). Reloading in that case is exactly what produced the
  // infinite login-page reload loop, so bail out and let the caller's own
  // `.catch` handle the error (e.g. show the "invalid credentials" toast).
  if (!shouldClearSession(error, localStorage.getItem("accessToken"))) {
    return;
  }

  // A single page can fire many requests that all 401 together (e.g. a
  // dashboard's widgets). Only the first should tear down + reload; the rest
  // no-op. The flag resets naturally on the page reload below.
  if (isHandlingUnauthorized) {
    return;
  }
  isHandlingUnauthorized = true;

  // Save current URL before clearing session (so login can bounce back here).
  saveIntendedUrl();

  // Clear the ACTUAL session keys. Previously only "user"/"refreshToken" were
  // removed, but the live token lives under "accessToken"/"userInfo" — so it
  // survived the reload, App.js#loadActiveIssuingCompany re-fired an
  // authenticated request, got another 401, and reloaded again → infinite loop.
  localStorage.removeItem("accessToken");
  localStorage.removeItem("userInfo");
  localStorage.removeItem("user");
  localStorage.removeItem("refreshToken");
  document.cookie = "user=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";
  window.location.reload();
};

export { handleUnauthorized };

const axiosInstance = axios.create();

axiosInstance.interceptors.response.use(
  (response) => {
    checkAppVersion(response);

    const lastAttempted = sessionStorage.getItem('lastAttemptedRedirect');
    if (lastAttempted) {
      console.log(`[Redirect] Successfully accessed ${lastAttempted} - clearing redirect tracking`);
      sessionStorage.removeItem('lastAttemptedRedirect');
      sessionStorage.removeItem('redirectAfterLogin');
      sessionStorage.removeItem('redirectAttempts');
    }

    return response;
  },
  (error) => {
    if (error.response) {
      checkAppVersion(error.response);

      if (error.response.status === 401) {
        handleUnauthorized(error);
      }
    }
    return Promise.reject(error);
  }
);

const api = (url = "", method, data = {}, requestConfig = {}) => {
  if (url.includes("?")) {
    const updatedURL1 = url?.split("?")[0];
    const updatedURL2 = url?.split("?")[1];
    const string = updateURL(updatedURL2);
    url = `${updatedURL1}?${string}`;
  }

  // Axios 1 serializes FormData as JSON when it is labelled application/json,
  // replacing File values with {}. Keep file requests multipart; the browser
  // adapter supplies the boundary. Ordinary object payloads remain JSON.
  const isFormData = typeof FormData !== "undefined" && data instanceof FormData;
  const options = {
    method,
    ...requestConfig,
    headers: {
      "content-type": isFormData ? "multipart/form-data" : "application/json",
      "x-access-token": `${localStorage.getItem("accessToken")}`,
      ...(requestConfig?.headers || {}),
    },
    data,
    url: `${baseURL}/${url}`,
  };
  return axiosInstance(options);
};

export default api;

const multipartApi = (url = "", method, data = {}, requestConfig = {}) => {
  if (url.includes("?")) {
    const updatedURL1 = url?.split("?")[0];
    const updatedURL2 = url?.split("?")[1];
    const string = updateURL(updatedURL2);
    url = `${updatedURL1}?${string}`;
  }

  const options = {
    ...requestConfig,
    method,
    headers: {
      "content-type": "multipart/form-data",
      "x-access-token": `${localStorage.getItem("accessToken")}`,
      "x-client-type": "admin",
    },
    data,
    url: `${baseURL}/${url}`,
  };
  return axiosInstance(options);
};

export { multipartApi };

const formDataApi = (url = "", method, data = {}) => {
  if (url.includes("?")) {
    const updatedURL1 = url?.split("?")[0];
    const updatedURL2 = url?.split("?")[1];
    const string = updateURL(updatedURL2);
    url = `${updatedURL1}?${string}`;
  }

  const options = {
    method,
    headers: {
      "x-access-token": `${localStorage.getItem("accessToken")}`,
      "x-client-type": "admin",
    },
    data,
    url: `${baseURL}/${url}`,
  };
  return axiosInstance(options);
};

export { formDataApi };

const refreshTokenApi = (url = "", method, data = {}) => {
  const options = {
    method,
    headers: {
      "content-type": "application/json",
      "x-access-token": `${localStorage.getItem("refreshToken")}`,
      "x-client-type": "admin",
    },
    data,
    url: `${baseURL}/${url}`,
  };
  return axiosInstance(options);
};

export { refreshTokenApi };
